import type { Dtos } from "@inrev/common";
import { ClickableText, Icon } from "@portal/components/primitives";

type IndemnitorCardProps = {
	type: "company" | "individual";
	name: string;
	number: number;
	signerEmail?: string;
	completed?: boolean;
	showESignatureDetails: boolean;
	onAddAddress?: () => void;
};

const IndemnitorCard = ({
	type,
	name,
	number,
	signerEmail,
	completed,
	showESignatureDetails,
	onAddAddress,
}: IndemnitorCardProps) => {
	return (
		<div className="grid auto-rows-auto grid-cols-[auto_auto_1fr_auto] gap-x-[10px] gap-y-[2px] w-full text-[13.5px] text-primary font-medium bg-surface-neutral border border-bc-default rounded-[3px] px-[12px] py-[8px]">
			<span className="font-medium text-[12px] text-secondary self-center">{number}.</span>
			{type === "company" && <Icon type="building-office" size="mini" className="self-center" />}
			{type === "individual" && <Icon type="user" size="mini" className="self-center" />}
			<span className="flex-1 self-center flex items-center gap-[10px]">
				{name}
				{onAddAddress && (
					<span className="flex items-center gap-[4px]" data-testid="indemnitor-missing-address">
						<Icon type="exclamation-triangle" color="warning" size="mini" className="shrink-0" />
						<ClickableText
							type="body"
							color="link"
							onClick={onAddAddress}
							data-testid="indemnitor-missing-address-action"
						>
							Add an address
						</ClickableText>
					</span>
				)}
			</span>
			{showESignatureDetails && (
				<>
					{completed !== undefined && !completed && (
						<span className="italic text-secondary font-normal text-[13px] self-center">
							Hasn't signed
						</span>
					)}
					{completed && (
						<span className="text-confirm font-medium text-[13px] self-center">Signed</span>
					)}
				</>
			)}
			{showESignatureDetails && !!signerEmail && (
				<>
					<div className="col-span-2" />
					<div className="col-span-2 text-[13px] font-normal text-tertiary">{signerEmail}</div>
				</>
			)}
		</div>
	);
};

export type AccountIndemnitorListProps = {
	indemnitors: Dtos.SuretyAccount.IndemnityAgreement.Get.Response["indemnitors"];
	showESignatureDetails: boolean;
	/** Companies to flag for a missing address, with where the agent adds it. */
	missingAddress?: { companyIds: ReadonlySet<string>; onAddAddress: () => void };
};

export const AccountIndemnitorList = ({
	indemnitors,
	showESignatureDetails,
	missingAddress,
}: AccountIndemnitorListProps) => {
	return (
		<div className="flex flex-col gap-[10px]">
			{indemnitors.companies.map((indemnitor, index) => (
				<IndemnitorCard
					key={indemnitor.id}
					type="company"
					name={indemnitor.name}
					number={index + 1}
					signerEmail={indemnitor.signerEmail}
					completed={indemnitor.completed}
					showESignatureDetails={showESignatureDetails}
					onAddAddress={
						missingAddress?.companyIds.has(indemnitor.id) ? missingAddress.onAddAddress : undefined
					}
				/>
			))}
			{indemnitors.individuals.map((indemnitor, index) => (
				<IndemnitorCard
					key={indemnitor.id}
					type="individual"
					name={indemnitor.name}
					number={indemnitors.companies.length + index + 1}
					signerEmail={indemnitor.signerEmail}
					completed={indemnitor.completed}
					showESignatureDetails={showESignatureDetails}
				/>
			))}
		</div>
	);
};
